import { Tooltip, TooltipContent, TooltipTrigger } from '@canyon/ui/Tooltip'
import { cn } from '@canyon/ui/utils'
import { type VariantProps, cva } from 'class-variance-authority'

export interface ITagProps extends React.ComponentProps<'div'> {
  border?: boolean
  rounded?: boolean
  size?: VariantProps<typeof tagVariants>['size']
  tooltip?: React.ReactNode | string
  variant?: VariantProps<typeof tagVariants>['variant']
}

const tagVariants = cva(
  'flex w-fit flex-row items-center gap-1.5 rounded-lg py-1 pl-2 pr-2.5 [&>svg]:shrink-0 flex-nowrap truncate cursor-default',
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'text-sm [&>svg]:size-4',
        sm: 'text-xs [&>svg]:size-3 py-0.5 pl-1.5 pr-2',
        xs: 'text-[10px] [&>svg]:size-2.5 py-0.5 pl-1.5 pr-2',
      },
      variant: {
        amber: 'bg-spectrum-amber-subtle text-spectrum-amber-light',
        blue: 'bg-spectrum-blue-subtle text-spectrum-blue-light',
        cyan: 'bg-spectrum-cyan-subtle text-spectrum-cyan-light',
        // Default variant
        default: 'bg-background-secondary text-white',
        emerald: 'bg-spectrum-emerald-subtle text-spectrum-emerald-light',
        fuchsia: 'bg-spectrum-fuchsia-subtle text-spectrum-fuchsia-light',
        green: 'bg-spectrum-green-subtle text-spectrum-green-light',
        indigo: 'bg-spectrum-indigo-subtle text-spectrum-indigo-light',
        lime: 'bg-spectrum-lime-subtle text-spectrum-lime-light',
        orange: 'bg-spectrum-orange-subtle text-spectrum-orange-light',
        pink: 'bg-spectrum-pink-subtle text-spectrum-pink-light',
        purple: 'bg-spectrum-purple-subtle text-spectrum-purple-light',
        red: 'bg-spectrum-red-subtle text-spectrum-red-light',
        sky: 'bg-spectrum-sky-subtle text-spectrum-sky-light',
        teal: 'bg-spectrum-teal-subtle text-spectrum-teal-light',
        violet: 'bg-spectrum-violet-subtle text-spectrum-violet-light',
        yellow: 'bg-spectrum-yellow-subtle text-spectrum-yellow-light',
      },
    },
  }
)

const borderVariants = cva('', {
  defaultVariants: {
    variant: 'default',
  },
  variants: {
    variant: {
      amber: 'border-spectrum-amber-dark',
      blue: 'border-spectrum-blue-dark',
      cyan: 'border-spectrum-cyan-dark',
      default: 'border-spectrum-default-dark',
      emerald: 'border-spectrum-emerald-dark',
      fuchsia: 'border-spectrum-fuchsia-dark',
      green: 'border-spectrum-green-dark',
      indigo: 'border-spectrum-indigo-dark',
      lime: 'border-spectrum-lime-dark',
      orange: 'border-spectrum-orange-dark',
      pink: 'border-spectrum-pink-dark',
      purple: 'border-spectrum-purple-dark',
      red: 'border-spectrum-red-dark',
      sky: 'border-spectrum-sky-dark',
      teal: 'border-spectrum-teal-dark',
      violet: 'border-spectrum-violet-dark',
      yellow: 'border-spectrum-yellow-dark',
    },
  },
})

export const Tag = ({
  border,
  children,
  className,
  rounded,
  size,
  tooltip,
  variant,
  ...props
}: ITagProps) => {
  const tagElement = (
    <div
      className={cn(
        tagVariants({ size, variant }),
        rounded && 'rounded-full',
        border && 'border-0.5',
        border && borderVariants({ variant }),
        className
      )}
      {...props}
    >
      {children}
    </div>
  )

  // Only wrap with Tooltip if tooltip content exists
  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{tagElement}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    )
  }

  return tagElement
}
